import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import { Feature, LineString, MultiLineString } from "geojson";

import { ensurePreviewLayer, findHitsAlongSegment, removePreviewLayer, setPreviewFeatures } from "./snapPreview";

const DRAW_SOURCES = new Set(["mapbox-gl-draw-cold", "mapbox-gl-draw-hot"]);
const PREVIEW_SOURCE_ID = "snap-delete-preview-source";
const PREVIEW_LAYER_ID = "snap-delete-preview-layer";
const TRAIL_SOURCE_ID = "snap-delete-trail-source";
const TRAIL_LAYER_ID = "snap-delete-trail-layer";

type PreviewFeature = Feature<LineString | MultiLineString>;

interface SnapDeleteState {
  dragging: boolean;
  markedFeatureIds: Map<string, PreviewFeature>;
  trail: Array<[number, number]>;
  lastScreenPoint: [number, number] | null;
}

function trailFeatures(coords: Array<[number, number]>): Array<Feature<LineString>> {
  if (coords.length < 2) return [];
  return [
    {
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: coords },
    },
  ];
}

/**
 * Adds any draw features (on the active layer) that intersect the screen-space segment from the
 * last drag point to the current event point into the in-progress "to-delete" set and re-renders
 * the preview overlay. Using the full segment means a fast drag doesn't skip thin features
 * between onDrag ticks. No features are actually deleted until the user releases the mouse.
 */
function collectHitsIntoPreview(this: MapboxDraw.DrawCustomModeThis, state: SnapDeleteState, event: MapboxDraw.MapMouseEvent): void {
  const editingLayerId = this._ctx.options.editOptions?.editingLayerId;
  if (!editingLayerId) return;

  const currentScreenPoint: [number, number] = [event.point.x, event.point.y];
  const hits = findHitsAlongSegment(
    this.map,
    state.lastScreenPoint,
    currentScreenPoint,
    (f) => DRAW_SOURCES.has(f.source) && (f.properties?.user_layerId as string | undefined) === editingLayerId
  );
  state.lastScreenPoint = currentScreenPoint;
  if (hits.length === 0) return;

  let changed = false;
  for (const hit of hits) {
    const featureId = hit.properties?.id as string | undefined;
    if (!featureId) continue;
    if (state.markedFeatureIds.has(featureId)) continue;

    const drawFeature = this.getFeature(featureId);
    if (!drawFeature) continue;

    const geojson = drawFeature.toGeoJSON() as Feature;
    if (geojson.geometry.type !== "LineString" && geojson.geometry.type !== "MultiLineString") continue;

    state.markedFeatureIds.set(featureId, geojson as PreviewFeature);
    changed = true;
  }

  if (changed) {
    setPreviewFeatures(this.map, PREVIEW_SOURCE_ID, Array.from(state.markedFeatureIds.values()));
  }
}

/**
 * Custom draw mode that previews features for deletion while the user drags, and removes them
 * when the mouse is released. Scoped strictly to editOptions.editingLayerId so primary and
 * secondary footprints are deleted independently.
 */
const snapDeleteFeature: DrawCustomMode<SnapDeleteState> = {
  onSetup(): SnapDeleteState {
    this.updateUIClasses({ mouse: "pointer" });
    ensurePreviewLayer(this.map, PREVIEW_SOURCE_ID, PREVIEW_LAYER_ID, {
      "line-color": "#d50000",
      "line-width": 6,
      "line-opacity": 0.6,
      "line-dasharray": [2, 2],
    });
    ensurePreviewLayer(this.map, TRAIL_SOURCE_ID, TRAIL_LAYER_ID, {
      "line-color": "#d50000",
      "line-width": 2,
      "line-opacity": 0.9,
      "line-dasharray": [1, 1],
    });
    return { dragging: false, markedFeatureIds: new Map(), trail: [], lastScreenPoint: null };
  },

  onMouseDown(state: SnapDeleteState, event: MapboxDraw.MapMouseEvent) {
    // Only react to primary (left) button — let middle/right pass through so the user can still
    // pan and context-click the map while the mode is active.
    if (event.originalEvent.button !== 0) return;
    state.dragging = true;
    state.markedFeatureIds = new Map();
    state.trail = [[event.lngLat.lng, event.lngLat.lat]];
    state.lastScreenPoint = null;
    this.map.dragPan.disable();
    collectHitsIntoPreview.call(this, state, event);
  },

  onDrag(state: SnapDeleteState, event: MapboxDraw.MapMouseEvent) {
    if (!state.dragging) return;
    state.trail.push([event.lngLat.lng, event.lngLat.lat]);
    setPreviewFeatures(this.map, TRAIL_SOURCE_ID, trailFeatures(state.trail));
    collectHitsIntoPreview.call(this, state, event);
  },

  onMouseUp(state: SnapDeleteState) {
    if (!state.dragging) return;
    state.dragging = false;
    this.map.dragPan.enable();

    const ids = Array.from(state.markedFeatureIds.keys());
    state.markedFeatureIds = new Map();
    state.trail = [];
    state.lastScreenPoint = null;
    setPreviewFeatures(this.map, PREVIEW_SOURCE_ID, []);
    setPreviewFeatures(this.map, TRAIL_SOURCE_ID, []);
    if (ids.length > 0) {
      this.deleteFeature(ids as unknown as string);
    }
  },

  onStop() {
    this.map.dragPan.enable();
    removePreviewLayer(this.map, PREVIEW_SOURCE_ID, PREVIEW_LAYER_ID);
    removePreviewLayer(this.map, TRAIL_SOURCE_ID, TRAIL_LAYER_ID);
  },

  toDisplayFeatures(_state, geojson, display) {
    display(geojson);
  },
};

export default snapDeleteFeature;
